import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api/index.ts";
import { User } from "./user";
import { Note } from "./notes";

export interface Escalation {
  id: string;
  mentee: User;
  mentor: User;
  status: "OPEN" | "CLOSED";
  created_at: string;
  updated_at: string;
  reason?: string;
  metadata?: Record<string, any>;
  started_by?: User;
  closed_by?: User;
  closed_at?: string;
  supervisor_note?: Note;
}

export interface EscalationListItem extends Escalation {
  mentorship: {
    id: string;
  };
}

export interface GetAllEscalationsResponse {
  escalations: EscalationListItem[];
  page: number;
  limit: number;
  is_last_page: boolean;
}

export const useGetCurrentEscalation = (menteeId?: string) => {
  return useQuery<Escalation>({
    queryKey: ["escalation", menteeId],
    queryFn: async () => {
      const escalation = await fetchApi<{ escalation: Escalation }>({
        url: `/v2/escalations/mentee/${menteeId}`,
        method: "GET",
      });

      if (!escalation.data) throw new Error("No escalation found");

      return escalation.data.escalation;
    },
    enabled: !!menteeId,
  });
};

export const useGetAllEscalations = (page: number = 1, limit: number = 10) => {
  return useQuery<GetAllEscalationsResponse>({
    queryKey: ["escalations", { page, limit }],
    queryFn: async () => {
      const response = await fetchApi<GetAllEscalationsResponse>({
        url: `/v2/escalations?page=${page}&limit=${limit}`,
        method: "GET",
      });

      if (!response.data) throw new Error("No escalations found");

      return response.data;
    },
  });
};

export const useCreateEscalation = (menteeId: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    Escalation,
    Error,
    { threat: boolean; mentorshipId: string }
  >({
    mutationKey: ["escalations-create"],
    mutationFn: async ({ threat, mentorshipId }) => {
      const escalation = await fetchApi<Escalation>({
        url: "/v2/escalations",
        method: "POST",
        data: {
          mentorshipId,
          reason: threat ? "911" : "NON_911",
        },
      });

      if (!escalation.data) throw new Error("Escalation not created");

      return escalation.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["escalation", menteeId],
      });
    },
  });
};

interface UpdateEscalationParams {
  id: string;
  status?: "CLOSED";
  reason?: string;
  metadata?: Record<string, any>;
}

export const useUpdateEscalation = (menteeId: string) => {
  const queryClient = useQueryClient();

  return useMutation<Escalation, Error, UpdateEscalationParams>({
    mutationKey: ["escalations-update"],
    mutationFn: async (data) => {
      const escalation = await fetchApi<Escalation>({
        url: `/v2/escalations/${data.id}`,
        method: "PATCH",
        data: {
          status: data.status,
          reason: data.reason,
          metadata: data.metadata,
        },
      });

      if (!escalation.data) throw new Error("Escalation not updated");

      return escalation.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["escalation", menteeId],
      });
    },
  });
};
